import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { useUser } from '../../../../../context/UserContext';
import { useMailingPostage, PostageDelay, PostageProduct, PostageZone } from '../../../../../hooks/Marketing';
import { canSeeQuotation, useRights } from '../../../../../rights';
import type { TargetingFormData } from '../CampaignTargetingStep';
import type { ValidationActions } from '../editor/useValidationActions';

interface MailingPostageCardProps {
  targeting: TargetingFormData;
  // Date de dépôt poste (AAAA-MM-JJ)
  sendDate: string;
  actions: ValidationActions;
}

interface PostageLine {
  zone: PostageZone;
  products: PostageProduct[];
  product: PostageProduct;
  total: number;
}

const formatEuro = (value: number, digits = 2) =>
  `${value.toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits })} €`;

// « J+4 » → date de dépôt + 4 jours (BeOne : date d'arrivée des courriers)
const estimatedArrival = (sendDate: string, delayName: string): string | null => {
  const days = Number(delayName.split('+')[1]);
  if (!sendDate || !Number.isFinite(days)) return null;
  const date = new Date(`${sendDate.slice(0, 10)}T12:00:00`);
  date.setDate(date.getDate() + days);
  return date.toLocaleDateString('fr-FR');
};

// Produit retenu par zone : celui déjà choisi s'il est proposé pour ce délai, sinon le moins cher
const defaultProducts = (delay: PostageDelay | undefined, selectedIds: number[]): Record<string, number> =>
  Object.fromEntries(Object.entries(delay?.products ?? {}).map(([zoneId, products]) => [
    zoneId,
    (products.find(product => selectedIds.includes(product.zoneDelayId)) ?? products[0]).zoneDelayId,
  ]));

function MailingPostageCard({ targeting, sendDate, actions }: MailingPostageCardProps) {
  const { selectedWorkspace } = useUser();
  const { rights } = useRights();
  const showQuotation = canSeeQuotation(rights, selectedWorkspace?.volumeName);
  // Les zones sont calculées sur le résultat d'une requête : pas de tarif pour une liste importée
  const queryId = targeting.listId === null ? targeting.queryId : null;
  const { options, loading, error } = useMailingPostage(queryId);
  const { postageProductIds, changePostageProducts } = actions;
  const [delayId, setDelayId] = useState<number | null>(null);
  const [productByZone, setProductByZone] = useState<Record<string, number>>({});

  // À chaque chargement des tarifs : délai du choix précédent s'il est encore proposé, sinon le plus rapide
  useEffect(() => {
    const delay = options.delays.find(d => Object.values(d.products).flat().some(p => postageProductIds.includes(p.zoneDelayId)))
      ?? options.delays[0];
    setDelayId(delay?.id ?? null);
    setProductByZone(defaultProducts(delay, postageProductIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- le choix précédent ne sert qu'à l'initialisation
  }, [options]);

  const delay = options.delays.find(d => d.id === delayId);
  const lines = useMemo<PostageLine[]>(() => {
    if (!delay) return [];
    return options.zones.map(zone => {
      const products = delay.products[zone.zoneId] ?? [];
      const product = products.find(p => p.zoneDelayId === productByZone[zone.zoneId]) ?? products[0];
      return { zone, products, product, total: Math.round(zone.quantity * product.unitPrice * 100) / 100 };
    });
  }, [delay, options.zones, productByZone]);

  const selectedIds = lines.map(line => line.product.zoneDelayId).join(',');
  useEffect(() => {
    changePostageProducts(selectedIds ? selectedIds.split(',').map(Number) : []);
  }, [selectedIds, changePostageProducts]);

  const totalHt = lines.reduce((sum, line) => sum + line.total, 0);
  const totalVat = lines.reduce((sum, line) => sum + line.total * (line.product.vatValue ?? 0) / 100, 0);
  const arrival = delay ? estimatedArrival(sendDate, delay.name) : null;

  const changeDelay = (id: number) => {
    const next = options.delays.find(d => d.id === id);
    setDelayId(id);
    setProductByZone(defaultProducts(next, lines.map(line => line.product.zoneDelayId)));
  };

  const renderContent = () => {
    if (queryId === null) {
      return <Alert severity="info">L'affranchissement est calculé à partir d'une requête de ciblage.</Alert>;
    }
    if (loading) {
      return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>;
    }
    if (error) return <Alert severity="error">{error}</Alert>;
    if (options.zones.length === 0) {
      return <Alert severity="warning">Aucun destinataire de la requête n'a d'adresse postale exploitable.</Alert>;
    }
    if (!delay) {
      return <Alert severity="warning">Aucun tarif d'affranchissement en vigueur ne couvre l'ensemble des destinataires.</Alert>;
    }

    return (
      <>
        <TextField
          select
          size="small"
          fullWidth
          label="Délai moyen d'acheminement"
          value={delay.id}
          onChange={e => changeDelay(Number(e.target.value))}
        >
          {options.delays.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
        </TextField>
        {arrival && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Arrivée estimée des courriers : {arrival}
          </Typography>
        )}

        <Box sx={{ overflowX: 'auto', mt: 2 }}>
          <Table size="small" sx={{ '& td, & th': { px: 1, fontSize: '0.75rem' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Zone</TableCell>
                <TableCell>Affranchissement</TableCell>
                <TableCell align="right">Clients</TableCell>
                <TableCell align="right">Prix unit. HT</TableCell>
                <TableCell align="right">Montant HT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map(line => (
                <TableRow key={line.zone.zoneId}>
                  <TableCell>{line.zone.name}</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    {line.products.length > 1 ? (
                      <TextField
                        select
                        size="small"
                        variant="standard"
                        fullWidth
                        value={line.product.zoneDelayId}
                        onChange={e => setProductByZone(prev => ({ ...prev, [line.zone.zoneId]: Number(e.target.value) }))}
                        slotProps={{ input: { sx: { fontSize: '0.75rem' } } }}
                      >
                        {line.products.map(p => <MenuItem key={p.zoneDelayId} value={p.zoneDelayId}>{p.name}</MenuItem>)}
                      </TextField>
                    ) : line.product.name}
                  </TableCell>
                  <TableCell align="right">{line.zone.quantity.toLocaleString('fr-FR')}</TableCell>
                  <TableCell align="right">{formatEuro(line.product.unitPrice, 3)}</TableCell>
                  <TableCell align="right">{formatEuro(line.total)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={4} sx={{ fontWeight: 700 }}>Total HT</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{formatEuro(totalHt)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={4} sx={{ fontWeight: 700, borderBottom: 0 }}>Total TTC</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, borderBottom: 0 }}>{formatEuro(totalHt + totalVat)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </>
    );
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, backgroundColor: 'grey.50' }}>
      <CardContent>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Affranchissement</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Tarifs postaux en vigueur pour une lettre de {options.weight || 20} g maximum, refacturés au réel.
        </Typography>

        {renderContent()}

        {showQuotation && (
          <Button
            variant="outlined"
            fullWidth
            size="large"
            startIcon={actions.downloadingDevis ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
            disabled={actions.downloadingDevis || loading}
            onClick={actions.downloadDevis}
            sx={{ mt: 2, textTransform: 'none', fontWeight: 600, color: 'primary.main', borderColor: 'primary.main' }}
          >
            {actions.downloadingDevis ? 'GÉNÉRATION EN COURS…' : 'VOIR LE DEVIS'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default MailingPostageCard;
